import { useEffect, useRef } from 'react'
import { router } from '@/routes'
import { useStartParamStore, initStartParam } from '@/store/useStartParamStore'

/**
 * StartParamRouter
 *
 * App-effect component that handles universal startapp parameter routing.
 * Works for both Telegram Mini App (tgWebAppStartParam) and browser (?startapp=XXX).
 *
 * On mount: extracts the startparam and resolves it to a target route.
 * Then subscribes to router navigation events — when the user lands on /dashboard
 * and a pending startparam route exists, it immediately redirects there.
 *
 * This catches all login/signup success paths that navigate to /dashboard
 * without needing to modify each auth callback individually.
 *
 * The TelegramSessionInitializer and AuthSessionWrapper are modified separately
 * to check for pending routes, since they fire before or outside the router.
 */
export const StartParamRouter = () => {
  const initialized = useRef(false)

  // Extract and store the startparam on mount (once)
  useEffect(() => {
    if (initialized.current) return
    initialized.current = true
    initStartParam()
  }, [])

  // Subscribe to router navigation events to intercept /dashboard navigations
  useEffect(() => {
    const unsubscribe = router.subscribe((state) => {
      const { getPendingRoute, markConsumed } = useStartParamStore.getState()
      const pendingRoute = getPendingRoute()

      if (!pendingRoute) return

      // When the router navigates to /dashboard and we have a pending route,
      // immediately redirect to the target
      if (state.location.pathname === '/dashboard') {
        router.navigate(pendingRoute, { replace: true })
        markConsumed()
      }
    })

    return unsubscribe
  }, [])

  return null
}
